import { IMedia } from '@dtos/Course'
import { View, XStack, Text, isWeb, Spinner } from 'tamagui'
import { HeroIcon } from '@components/HeroIcon'
import { Button } from '@components/Button'
import { useTranslation } from 'react-i18next'
import { openWebBrowser } from '@utils/openWebBrowser'
import { documentDirectory, downloadAsync } from 'expo-file-system'
import { useCallback, useState } from 'react'
import { shareAsync } from 'expo-sharing'
import { useToastController } from '@tamagui/toast'

interface INotSupportedProps {
  media: IMedia
  fileUrl?: string
}

export function NotSupported({ media, fileUrl }: INotSupportedProps) {
  const { t } = useTranslation('ExtraContent')
  const [isDownloading, setIsDownloading] = useState(false)
  const toast = useToastController()

  const handlePressDownload = useCallback(
    async (fileUrlValue: string, shouldOpenInNewTab: boolean) => {
      if (!fileUrlValue) return

      if (isWeb) {
        openWebBrowser(fileUrlValue, shouldOpenInNewTab)
        return
      }

      try {
        setIsDownloading(true)

        const fileName =
          fileUrlValue.split('/').pop() || `file.${media.fileExt}`
        const localURL = `${documentDirectory}${fileName}`

        await downloadAsync(fileUrlValue, localURL)
        await shareAsync(localURL)
      } catch (error) {
        toast.show('Erro ao baixar arquivo!', {
          message:
            'Houve um erro ao baixar o arquivo. Por favor, tente novamente.',
          type: 'error',
        })
      } finally {
        setIsDownloading(false)
      }
    },
    [media.fileExt, toast],
  )

  const isVideo = media?.type === 'Video'
  const setColor = isVideo ? '$neutral-500' : '$primary-300'

  if (isWeb) {
    return (
      <View
        alignItems="center"
        gap="$spacing-inset-xs"
        $gtSm={{
          paddingHorizontal: 200,
          paddingVertical: 50,
        }}
      >
        <HeroIcon icon={['fal', 'download']} size={40} color="$neutral-800" />
        <Text
          color="$neutral-800"
          ff="$mulishRegular"
          fontSize="$font-size-xs"
          textAlign="center"
          whiteSpace="pre-line"
        >
          {t('NotSupported.title')}
        </Text>
        {media && (
          <Button
            variant="primary"
            testID="not-supported-download-button"
            outline
            onPress={() => fileUrl && handlePressDownload(fileUrl, true)}
            disabled={isVideo || isDownloading}
          >
            <XStack gap="$spacing-inset-nano" alignItems="center">
              {isDownloading ? (
                <Spinner size="small" color={setColor} />
              ) : (
                <HeroIcon
                  size={20}
                  icon={['fal', 'download']}
                  color={setColor}
                />
              )}
              <Text
                fontSize="$font-size-xxs"
                ff="$mulishBold"
                color={setColor}
                position="relative"
                testID="not-supported-button-text"
              >
                {isDownloading
                  ? t('NotSupported.downloading')
                  : t('NotSupported.download')}
              </Text>
            </XStack>
          </Button>
        )}
      </View>
    )
  }

  return (
    <View
      backgroundColor="$background-100"
      width="100%"
      height="100%"
      alignItems="center"
      justifyContent="center"
    >
      <View
        opacity={0.5}
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        justifyContent="center"
        alignItems="center"
      >
        <HeroIcon icon={['fal', 'file']} size={90} color="$primary-300" />
      </View>
      <Button
        variant="primary"
        testID="not-supported-download-button"
        justifyContent="center"
        onPress={() => fileUrl && handlePressDownload(fileUrl, false)}
        disabled={isDownloading || isVideo}
      >
        <XStack gap={8} alignItems="center" justifyContent="center">
          {isDownloading ? (
            <Spinner
              size="small"
              color={isDownloading ? '$neutral-500' : '$neutral-0'}
            />
          ) : (
            <HeroIcon icon={['fas', 'download']} size={20} color="$neutral-0" />
          )}
          <Text
            fontSize="$font-size-xs"
            ff="$mulishBold"
            fontWeight={700}
            color={isDownloading ? '$neutral-500' : '$neutral-0'}
            mt="$spacing-inset-quarck"
            testID="not-supported-button-text"
          >
            {isDownloading ? 'Baixando conteúdo' : 'Baixar conteúdo'}
          </Text>
        </XStack>
      </Button>
    </View>
  )
}
